// Binogi character names used when a session is in character-name mode.
// Sourced from contentScripts/chararacterPopularity (the names that actually
// appear in Binogi lesson transcripts), with generic roles, controversial
// historical figures, and obviously non-name entries filtered out.
//
// Order is appearance-count from the popularity CSV (case-insensitive sum),
// most popular first. `pickCharacterName` picks uniformly at random from a
// window (PICK_WINDOW) of the first free names, so ordering is only
// popularity-approximate — see PICK_WINDOW below for why a strict
// first-free rule doesn't scale to concurrent join waves.
// Only names with an M4 BINOGI character sheet in the image library make
// the list — every assignable character has a face in
// /packages/frontend/public/characters/<Name>.png.
// Look-alike pairs keep only the more popular name (Sophia not Sofia,
// Michael not Mikael) so two students never get names that read the same.
export const CHARACTER_NAMES = [
    "Leon",
    "Kim",
    "Philip",
    "Michael",
    "Lina",
    "Jenny",
    "Maria",
    "Seamus",
    "Tommy",
    "Sophia",
    "Selma",
    "Bob",
    "Aadesh",
    "Stella",
    "William",
    "Emma",
    "Iwette",
    "Danni",
    "Molly",
    "Joseph",
    "Adam",
    "Senait",
    "Rauno",
    "Paul",
    "Nina",
    "James",
    "Isabella",
    "Harald",
    "Eva",
    "Zayd",
    "Tara",
    "Susie",
    "Peter",
    "Cecilia",
    "Aki",
    "Shilpa",
    "Rebecca",
    "Rabia",
    "Niimi",
    "Malik",
    "John",
    "Esther",
    "Ellen",
    "Davina",
    "Claude",
];
// How many free candidates to collect before picking one at random. A
// strictly-first-free allocator means every concurrent joiner races the
// exact same NAME# row: N joiners fighting over one deterministic
// candidate collapses claim throughput to one name per DynamoDB
// round-trip, no matter how large N is — empirically, a 1,000-student
// join wave saw 432/1,001 join Lambdas hit their 10s timeout with only
// 33 names ever claimed. Spreading the pick uniformly over a window of
// W free candidates cuts expected collisions on any single name roughly
// W-fold (a joiner only collides with others who drew the same index),
// while still keeping early joiners on the top of the popularity-ordered
// catalogue rather than scattering them across the whole list.
export const PICK_WINDOW = 24;
// Pick uniformly at random from the first PICK_WINDOW unused names in the
// popularity-ordered list (see PICK_WINDOW above for why: this is what
// keeps concurrent joiners from convoying on one candidate). Once every
// character is taken the list is walked again with a numeric suffix —
// "Leon 2", "Kim 2", … then "Leon 3" and so on — and the window is filled
// from there too, still respecting `taken`. Passing `rng = () => 0`
// recovers the exact old first-free behavior (index 0 of the free list),
// which some tests and the `preferred`-name fast path lean on. `rng` is
// expected to return a value in [0, 1) like `Math.random`; an
// out-of-range value (e.g. a caller-injected rng returning >= 1) is
// clamped to the last candidate rather than indexing out of bounds.
//
// Callers loop on this (reserveCharacterName retries after a NAME#
// reservation conflict), and a fallback that returned the same string
// every time made those retries spin on a name already known to be gone
// until the join gave up with an internal error — so `candidates` must
// only ever contain names not in `taken`.
export function pickCharacterName(taken, rng = Math.random) {
    if (CHARACTER_NAMES.length === 0)
        return "Player";
    const candidates = [];
    for (const name of CHARACTER_NAMES) {
        if (!taken.has(name)) {
            candidates.push(name);
            if (candidates.length >= PICK_WINDOW)
                break;
        }
    }
    if (candidates.length < PICK_WINDOW) {
        // Seed the overflow pass near where free slots actually are: with
        // ~700 reserved names, joiner #701 should probe around "… 15", not
        // walk the hundreds of suffixed names already known to be taken.
        // Never below 2 — suffix "1" is the bare catalogue name handled by
        // the pass above. The seed can skip a sparse earlier suffix; that
        // only costs cosmetic ordering, and isCharacterName accepts any
        // suffix to 999. This bounds probing to O(catalogue + window) in the
        // typical case; adversarially scattered free slots bound it at
        // window × catalogue probes (worst case: one free slot per suffix
        // cycle) — never O(taken), since the seed still jumps straight near
        // the free frontier instead of walking every already-taken suffixed
        // name.
        const startSuffix = Math.max(2, Math.floor(taken.size / CHARACTER_NAMES.length) + 1);
        outer: for (let suffix = startSuffix;; suffix++) {
            for (const name of CHARACTER_NAMES) {
                const candidate = `${name} ${suffix}`;
                if (!taken.has(candidate)) {
                    candidates.push(candidate);
                    if (candidates.length >= PICK_WINDOW)
                        break outer;
                }
            }
        }
    }
    const index = Math.min(candidates.length - 1, Math.floor(rng() * candidates.length));
    return candidates[index];
}
// The exact overflow forms `pickCharacterName` can emit: a single space,
// then 2…999 with no leading zeros. Anything looser ("Leon 0", "Leon 1",
// "Leon 007", "Leon 1337") is a client inventing a variant the allocator
// would never produce — and every variant we accept is one more slot a
// crafted client can burn out of the catalogue.
const OVERFLOW_SUFFIX = / ([2-9]|[1-9]\d{1,2})$/;
// True for names this module can hand out — a catalogue entry, or one of
// the numbered overflow forms above. Used to vet a client-supplied
// displayName in character-name mode: that mode exists so a student can't
// put arbitrary text on the teacher's projected leaderboard, so a
// requested name is only honoured if the allocator could have produced it.
export function isCharacterName(name) {
    if (CHARACTER_NAMES.includes(name))
        return true;
    const m = OVERFLOW_SUFFIX.exec(name);
    if (!m)
        return false;
    return CHARACTER_NAMES.includes(name.slice(0, m.index));
}
// Used only for the teacher-side preview "Try: Leon, Kim, Philip" hint.
export function sampleCharacterPreview(count, _rng = Math.random) {
    return CHARACTER_NAMES.slice(0, count).map((n) => n);
}
//# sourceMappingURL=characterNames.js.map